import React, { useState, useEffect } from "react";
import { POPUP_TRIGGER_EVENT } from "@/hooks/use-popup";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Headphones, Car, BadgePercent, X } from "lucide-react";

const PopupForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    customer_type: "",
    city: "",
    message: "",
    consent: false,
    source: "popup_form",
  });

  // Open popup on trigger event or after 10s
  useEffect(() => {
    const handleTrigger = () => setIsOpen(true);
    window.addEventListener(POPUP_TRIGGER_EVENT, handleTrigger);
    const timer = setTimeout(() => setIsOpen(true), 10000);

    return () => {
      window.removeEventListener(POPUP_TRIGGER_EVENT, handleTrigger);
      clearTimeout(timer);
    };
  }, []);

  // Lock body scroll when popup is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);

    const { name, mobile, email, customer_type, consent } = form;

    if (!name || !mobile || !email || !customer_type) {
      setFeedback({ type: "error", message: "Please fill all required fields." });
      return;
    }
    if (!mobile.startsWith("+")) {
      setFeedback({ type: "error", message: "Mobile number must start with + (country code)." });
      return;
    }
    if (!consent) {
      setFeedback({ type: "error", message: "Please provide consent to continue." });
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(
        "https://abheecelestialcityphase2.in/api-property/api/site_visit_request.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        }
      );
      const data = await res.json();

      if (data.status === "success") {
        setFeedback({ type: "success", message: "Request submitted successfully!" });
        setForm({
          name: "",
          mobile: "",
          email: "",
          customer_type: "",
          city: "",
          message: "",
          consent: false,
          source: "popup_form",
        });
        setTimeout(() => setIsOpen(false), 1500);
      } else {
        setFeedback({ type: "error", message: data.message || "Something went wrong." });
      }
    } catch (err) {
      setFeedback({ type: "error", message: "Server error. Please try again later." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
    <DialogContent
  className="
    p-0
    w-full
    max-w-md
    sm:max-w-[600px]
    h-[90vh]
    max-h-[90vh]
    bg-white
    border-none
    z-[9999]
    flex
    flex-col
    pointer-events-auto
  "
  showCloseButton={false}
>
        {/* Sticky Header with Close Button */}
        <div className="bg-[#323992] py-3 px-4 flex items-center justify-center relative z-[1200] sticky top-0">
          <h2 className="text-white text-lg font-bold uppercase tracking-wider">
            Request A Site Visit
          </h2>
          <button
            onClick={() => setIsOpen(false)}
            className="absolute right-4 text-white/80 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="https://slelguoygbfzlpylpxfs.supabase.co/storage/v1/object/public/test-clones/01094675-c23f-432c-b065-2c82ff5c3d40-india-sales-office-com/assets/images/logo-1.png"
              alt="Abhee Celestial City"
              className="h-16 w-auto"
            />
          </div>

          {/* Features */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              { icon: Headphones, label: "Instant Call Back" },
              { icon: Car, label: "Free Site Visit" },
              { icon: BadgePercent, label: "Best Price" },
            ].map((f, i) => (
              <div key={i} className="flex flex-col items-center text-center group">
                <div className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center mb-2 group-hover:border-[#323992] transition-colors">
                  <f.icon className="text-gray-600 group-hover:text-[#323992]" size={24} />
                </div>
                <span className="text-[12px] font-medium text-gray-700">{f.label}</span>
              </div>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Input placeholder="Name *" value={form.name} onChange={(e) => handleChange("name", e.target.value)} />
              <Input placeholder="Mobile (+country code) *" value={form.mobile} onChange={(e) => handleChange("mobile", e.target.value)} />
              <Input placeholder="Email *" type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} />
<Select
  value={form.customer_type}
  onValueChange={(val) => handleChange("customer_type", val)}
  modal={false}
>
  <SelectTrigger className="w-full cursor-pointer">
    <SelectValue placeholder="Customer type *" />
  </SelectTrigger>

  <SelectContent className="z-[10000]">
    <SelectItem value="individual">Individual</SelectItem>
    <SelectItem value="agent">Agent</SelectItem>
    <SelectItem value="corporate">Corporate</SelectItem>
  </SelectContent>
</Select>
              <Input placeholder="City" value={form.city} onChange={(e) => handleChange("city", e.target.value)} />
              <textarea
                placeholder="Message"
                className="border-b outline-none"
                value={form.message}
                onChange={(e) => handleChange("message", e.target.value)}
              />
            </div>

            {/* Consent */}
            <div className="flex items-start gap-3 mt-4">
              <Checkbox checked={form.consent} onCheckedChange={(v) => handleChange("consent", v)} />
              <label htmlFor="consent" className="text-[10px] leading-tight text-gray-600 cursor-pointer">
                I Consent to The Processing of Provided Data According To Privacy Policy | Terms & Conditions, 
                I Authorize Property Junction International Real Estate Broker LLC Private Limited and its 
                representatives to Call, SMS, Email or WhatsApp Me About Its Products and Offers. 
                This Consent Overrides Any Registration For DNC/NDNC.
              </label>
            </div>

            {/* Feedback */}
            {feedback && (
              <p className={`text-sm ${feedback.type === "success" ? "text-green-600" : "text-red-600"}`}>
                {feedback.message}
              </p>
            )}

            <Button disabled={loading} className="w-full">
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PopupForm;
